import { Box, TextField, Typography } from "@mui/material"
import React, {useContext, useEffect, useRef, useState} from "react";
import Split from "react-split";
import SequenceCanvasSection from "./canvas/SequenceCanvasSection";
import SequenceTimeline from "./timeline/SequenceTimeline";
import {SequenceCompositionService} from "./services/SequenceCompositionService";
import SequenceCompositionBridge from "./SequenceCompositionBridge";
import {SequenceMediaSidebar} from "./SequenceMediaSidebar";
import ContextMenu from "../librettoEditor/components/ContextMenu/ContextMenu";
import {useAssetStore} from "../storage/AssetStore";
import SequenceTrackPropertiesBar from "./SequenceTrackPropertiesBar";
import {ProjectAssetsStore} from "../state/ProjectAssetsStore";
import {SequencePersistenceService} from "./services/SequencePersistenceService";
import {AuthContext} from "../context/AuthContext";
import {SequenceStateStore} from "./store/SequenceStateStore";
import {SequenceApplicationStore} from "./store/SequenceApplicationStore";
import SequenceMultiSelectPropertiesBar from "./SequenceMultiSelectPropertiesBar";
import {autorun} from "mobx";
import { useTheme } from "@mui/material/styles";

type SequenceEditorProps = {
    setSequenceEditorOpen: React.Dispatch<React.SetStateAction<boolean>>;
    sequenceId: string;
};


export const DoneButton = ({onClick}) => {
    const buttonStyle = {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        height: "35px",
        width: "70px",
        padding: "0.75rem 1rem",
        border: "1px solid #e0e0e0",
        borderRadius: "10px",
        backgroundColor: "white",
        cursor: "pointer",
        transition: "border 0.2s ease, background-color 0.2s ease",
        fontSize: "0.95rem",
        background: '#1a1a1a',
        "&:hover": {
            border: "1px solid #c0c0c0",
            backgroundColor: "#1a1a1a",
            opacity: 0.9,
        },
        justifyContent: "center",
    };

    return (
        <Box sx={buttonStyle} onClick={onClick}>
            <Box sx={{color: "white", fontSize: "0.9rem"}}>Done</Box>
        </Box>
    );
};

export const AddMediaButton = ({onClick}) => {

    const buttonStyle = {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        height: "35px",
        width: "140px",
        padding: "0.75rem 1rem",
        border: "1px solid #e0e0e0",
        borderRadius: "10px",
        backgroundColor: "white",
        cursor: "pointer",
        transition: "border 0.2s ease, background-color 0.2s ease",
        fontSize: "0.95rem",
        background: 'white',
        "&:hover": {
            border: "1px solid #efefef",
            backgroundColor: "#fefefe",
            opacity: 0.9,
        },
        justifyContent: "center",
    };

    return (
        <Box sx={buttonStyle} onClick={onClick}>
            <Box sx={{color: "#121212", fontSize: "0.9rem"}}>Add Media</Box>
        </Box>
    );
};

export const SequenceEditor = ({setSequenceEditorOpen, sequenceId}: SequenceEditorProps) => {
    const muiTheme = useTheme();
    const isDarkMode = muiTheme.palette.mode === "dark";

    const [name, setName] = React.useState<string>("Untitled");
    const [editingName, setEditingName] = React.useState<boolean>(false);
    const [isRestoring, setIsRestoring] = React.useState<boolean>(true);

    const {RenameSequence, GetAssetUrl, GetTrackUrl} = useAssetStore();

    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const restorationStartedRef = useRef<string | null>(null); // Track which sequenceId we've started restoring

    const authContext = useContext(AuthContext);

    const [sizes, setSizes] = useState([80, 20]);
    const [showFilesPanel, setShowFilesPanel] = useState(false);
    const [selectedClipCount, setSelectedClipCount] = useState<number>(SequenceApplicationStore.selectedClipIds.length);

    useEffect(() => {
        const dispose = autorun(() => {
            setSelectedClipCount(SequenceApplicationStore.selectedClipIds.length);
        });
        return () => dispose();
    }, []);

    const handleDoneClick = () => {
        setSequenceEditorOpen(false);
    }

    // Hydrate composition title when selected composition is empty view
    useEffect(() => {
        if (!sequenceId) return;
        SequencePersistenceService.init(authContext.getUserId(), sequenceId, authContext.getCurrentWorkspaceId?.());
        const comps = ProjectAssetsStore.projectSequences || [];
        const comp = comps.find((c: any) => c.id === sequenceId);
        if (comp) {
            const current = comp.title || comp.name || "";
            setName(current);
        }
    }, [sequenceId, ProjectAssetsStore.projectSequences]);

    // Clear stores and dispose bridge on unmount
    useEffect(() => {
        return () => {
            SequenceCompositionBridge.dispose();
            SequenceCompositionService.cleanup();
            SequenceStateStore.reset();
            SequenceApplicationStore.reset();
            SequencePersistenceService.reset();
            restorationStartedRef.current = null;
        };
    }, []);

    // Initialize composition and restore from persistence - runs exactly once per sequenceId
    useEffect(() => {
        if (!sequenceId) {
            setIsRestoring(false);
            restorationStartedRef.current = null;
            return;
        }

        // Prevent duplicate restoration for the same sequenceId
        if (restorationStartedRef.current === sequenceId) {
            return;
        }

        // Reset restoration state if sequenceId changed
        if (restorationStartedRef.current !== null && restorationStartedRef.current !== sequenceId) {
            // Clean up previous sequence
            SequenceCompositionBridge.dispose();
            SequenceCompositionService.cleanup();
            SequenceStateStore.reset();
            SequenceApplicationStore.reset();
        }

        let cancelled = false;
        restorationStartedRef.current = sequenceId;
        setIsRestoring(true);

        const initializeAndRestore = async () => {
            try {
                // 1. Clear stores first
                SequenceStateStore.reset();
                SequenceApplicationStore.reset();

                // 2. Initialize persistence service
                SequencePersistenceService.init(authContext.getUserId(), sequenceId, authContext.getCurrentWorkspaceId?.());

                // 3. Initialize composition engine (doesn't require canvas)
                if (!SequenceCompositionService.isInitialized()) {
                    await SequenceCompositionService.initialize();
                }
                if (cancelled) return;

                // 4. Initialize bridge (sets up event listeners)
                SequenceCompositionBridge.init();

                // 5. Restore from persistence (bridge will skip persistence during restore)
                const resolveAssetUrl = async (assetId: string, type: 'audio'|'video'|'image') => {
                    let url;
                    if (type === "image") {
                        url = await GetAssetUrl({ assetId });
                        return url;
                    }
                    url = await GetTrackUrl({ trackId: assetId });
                    return url;
                };

                console.log("Calling restore from persistence")
                await SequenceCompositionService.restoreFromPersistence(sequenceId, resolveAssetUrl);
                console.log("Restore from persistence returned")
                setIsRestoring(false);
            } catch (error) {
                console.error("Error initializing/restoring sequence:", error);
                if (!cancelled) {
                    setIsRestoring(false);
                }
            }
        };

        initializeAndRestore();

        return () => {
            cancelled = true;
            // Don't reset restorationStartedRef here - let unmount cleanup handle it
        };
    }, [sequenceId, authContext, GetAssetUrl, GetTrackUrl]);

    // Show loading state while restoring
    if (isRestoring) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'var(--app-bg-canvas)',
                    height: '100%',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '14px',
                        color: 'var(--app-text-muted)',
                        fontFamily: 'Inter, sans-serif',
                    }}
                >
                    Loading sequence...
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--app-bg-canvas)',
                gap: '5px',
                height: '100%',
                width: '100%',
                overflow: 'hidden',
                padding: "16px",
            }}
        >
            {/* Topbar */}
            <Box sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "6px 8px",
                minHeight: "48px"
            }}>
                {/* Editable name on the left */}
                <Box sx={{display: "flex", alignItems: "center"}}>
                    {editingName ? (
                        <TextField
                            value={name}
                            autoFocus
                            variant="outlined"
                            size="small"
                            fullWidth={false}
                            onChange={(e) => setName(e.target.value)}
                            onBlur={async () => {
                                const newName = (name || "").trim();
                                if (newName && sequenceId) {
                                    try { await RenameSequence({ sequenceId, newName }); } catch {}
                                }
                                setEditingName(false);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (async () => {
                                        const newName = (name || "").trim();
                                        if (newName && sequenceId) {
                                            try { await RenameSequence({ sequenceId, newName }); } catch {}
                                        }
                                        setEditingName(false);
                                    })();
                                }
                                if (e.key === 'Escape') setEditingName(false);
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: 32,
                                    backgroundColor: 'var(--app-bg-subtle)',
                                    borderRadius: '6px',
                                    boxSizing: 'border-box',
                                    '& fieldset': { borderColor: 'var(--app-border-subtle)' },
                                    '&:hover fieldset': { borderColor: 'var(--app-border-muted)' },
                                    '&.Mui-focused fieldset': { borderColor: 'var(--app-border-muted)' },
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: '6px 8px',
                                    fontSize: '0.95rem',
                                },
                            }}
                        />
                    ) : (
                        <Typography
                            variant="subtitle1"
                            onClick={() => setEditingName(true)}
                            sx={{
                                fontSize: "0.95rem",
                                height: "32px",
                                padding: "0 8px",
                                borderRadius: "6px",
                                cursor: "text",
                                transition: "background-color 0.15s ease, border-color 0.15s ease",
                                border: "1px solid var(--app-border-subtle)",
                                backgroundColor: "var(--app-bg-subtle)",
                                display: "flex",
                                alignItems: "center",
                                boxSizing: "border-box",
                                "&:hover": { backgroundColor: "var(--app-bg-subtle)", borderColor: "var(--app-border-muted)" },
                            }}
                        >
                            {name}
                        </Typography>
                    )}
                </Box>
                <ContextMenu/>
                {/* Action buttons on the right */}
                <Box sx={{display: "flex", flexDirection: "row", gap: "8px"}}>
                    <AddMediaButton onClick={() => { setShowFilesPanel(!showFilesPanel); }}/>
                    <DoneButton onClick={handleDoneClick}/>
                </Box>
            </Box>

            {/* Body: main area + properties sidebar */}
            <Box sx={{
                display: "flex",
                flexDirection: "row",
                gap: "12px",
                flex: 1,
                minHeight: 0,
                minWidth: 0,
            }}>
                {/* Main editor area */}
                <Box sx={{
                    background: "var(--app-bg-surface)",
                    borderRadius: "10px",
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    boxShadow: "inset 0 0 0 1px var(--app-border-subtle)",
                }}>
                    <Split
                        sizes={sizes}
                        minSize={30}
                        gutterSize={1}
                        direction="vertical"
                        onDragEnd={setSizes}
                        style={{display: 'flex', flexDirection: 'column', height: '100%', width: '100%'}}
                        gutter={(_index, direction) => {
                            const gutter = document.createElement('div');
                            gutter.className = `gutter gutter-${direction}`;
                            gutter.style.cursor = 'row-resize';
                            gutter.style.transition = 'background-color 0.3s';
                            gutter.style.zIndex = '10';
                            gutter.style.backgroundColor = isDarkMode ? '#3C4554' : '#bfc5cf';
                            gutter.addEventListener('mouseover', () => gutter.style.backgroundColor = isDarkMode ? '#5A6576' : '#8a93a3');
                            gutter.addEventListener('mouseout', () => gutter.style.backgroundColor = isDarkMode ? '#3C4554' : '#bfc5cf');
                            return gutter;
                        }}
                    >

                        {/* Top pane (canvas) */}
                        <div style={{display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0}}>
                            {/* @ts-ignore */}
                            <SequenceCanvasSection canvasRef={canvasRef} containerRef={containerRef}/>
                        </div>

                        {/* Bottom pane (timeline) */}
                        <div style={{overflow: 'hidden', minHeight: 0, minWidth: 0, width: '100%', paddingTop: "30px", backgroundColor: 'var(--app-bg-surface)'}}>
                            <SequenceTimeline/>
                        </div>

                    </Split>
                </Box>

                {/* Properties sidebar: shows Files panel when Add Media is clicked */}
                <Box sx={{
                    width: "320px",
                    flex: "0 0 320px",
                    minHeight: 0,
                    borderRadius: "10px",
                    background: "transparent",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column"
                }}>
                    {!showFilesPanel && (
                        <Box sx={{flex: 1, minHeight: 0}}>
                            {selectedClipCount > 1 ? (
                                <SequenceMultiSelectPropertiesBar/>
                            ) : (
                                <SequenceTrackPropertiesBar/>
                            )}
                        </Box>
                    )}
                    {showFilesPanel && (
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            {/* @ts-ignore */}
                            <SequenceMediaSidebar onClose={() => setShowFilesPanel(false)}/>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    )

}
